<template>
  <v-container fluid class="mcp-page">
    <v-row>
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title class="d-flex align-center">
            <span>MCP Servers</span>
            <v-spacer />
            <v-btn color="primary" @click="openAddExternalServerDialog">Add External Server</v-btn>
          </v-card-title>
          <v-card-text>
            <div class="section-title">Installed External Servers</div>
            <div v-if="installedExternalServers.length === 0" class="text-body-2 text-medium-emphasis mb-3">
              No external MCP servers installed yet
            </div>
            <TransitionGroup name="external-server-list" tag="div" class="external-server-list">
              <UserMcpServerCard
                v-for="server in installedExternalServers"
                :key="server.name"
                :server="server"
                :selected="selectedServerName === server.name"
                @select="onServerSelected"
                @install="openInstallForExisting"
                @uninstall="uninstallServer"
                @enable="enableServer"
                @disable="disableServer"
                @connectOAuth="openOAuthDialog"
                @saveSecrets="openSecretsDialog"
                @deleteShared="deleteSharedExternalServer"
                @requestRemoval="openExternalRemovalRequestDialog"
              />
            </TransitionGroup>

            <div
              class="section-title section-title-with-action section-title-toggle"
              role="button"
              tabindex="0"
              @click="toggleAvailableExternalExpanded"
              @keydown.enter.prevent="toggleAvailableExternalExpanded"
              @keydown.space.prevent="toggleAvailableExternalExpanded"
            >
              <span>Available External Servers</span>
              <div class="section-title-toggle-meta">
                <span class="section-title-count">{{ availableExternalServers.length }}</span>
                <v-icon size="small">{{ availableExternalExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
            </div>
            <v-expand-transition>
              <div v-show="availableExternalExpanded">
                <div v-if="availableExternalServers.length === 0" class="text-body-2 text-medium-emphasis mb-3">
                  No shared external servers available to install
                </div>
                <TransitionGroup name="external-server-list" tag="div" class="external-server-list">
                  <UserMcpServerCard
                    v-for="server in availableExternalServers"
                    :key="server.name"
                    :server="server"
                    :selected="selectedServerName === server.name"
                    @select="onServerSelected"
                    @install="openInstallForExisting"
                    @connectOAuth="openOAuthDialog"
                    @saveSecrets="openSecretsDialog"
                    @deleteShared="deleteSharedExternalServer"
                    @requestRemoval="openExternalRemovalRequestDialog"
                  />
                </TransitionGroup>
              </div>
            </v-expand-transition>

            <div class="section-title section-title-with-action">
              <span>Platform Servers</span>
              <v-btn variant="text" size="small" @click="openPlatformRequestDialog">Request Platform Server</v-btn>
            </div>
            <template v-if="(this as any).isAdmin === true">
              <McpServerCard
                v-for="serverId in adminPlatformServerIds"
                :key="`platform-admin-${serverId}`"
                :serverId="serverId"
                @server-selected="onServerSelected"
              />
            </template>
            <template v-else>
              <UserMcpServerCard
                v-for="server in platformServers"
                :key="server.name"
                :server="server"
                :selected="selectedServerName === server.name"
                @select="onServerSelected"
              />
            </template>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <McpServerCarousel
          v-if="selectedServerName === null"
          :items="defaultCarouselItems"
          :perPage="3"
          :autoRotateMs="5000"
          :resumeDelayMs="8000"
          @select-server="onServerSelected"
          @install-server="openInstallForExisting"
          @connect-oauth="openOAuthDialog"
        />

        <v-card v-else>
          <v-card-title>Available Tools</v-card-title>
          <v-card-subtitle>{{ toolPaneSubtitle }}</v-card-subtitle>
          <v-card-text>
            <v-alert v-if="selectedServer && selectedServer.source === 'external' && !selectedServer.installed" type="info" variant="tonal">
              <div class="text-subtitle-2 mb-2">Server Not Installed</div>
              <div class="mb-3">Install this shared external server to add it to your account.</div>
              <v-btn color="primary" @click="openInstallForExisting(selectedServer.name)">Install</v-btn>
            </v-alert>

            <v-alert
              v-else-if="selectedServer && selectedServer.source === 'external' && selectedServer.authMode === 'oauth2' && selectedServer.authState !== 'connected'"
              type="warning"
              variant="tonal"
            >
              <div class="text-subtitle-2 mb-2">Authentication Required</div>
              <div class="mb-3">Connect OAuth to load and use this server's tools.</div>
              <v-btn color="primary" @click="openOAuthDialog(selectedServer.name)">Connect OAuth</v-btn>
            </v-alert>

            <template v-else-if="selectedServerName && selectedToolIds.length > 0">
              <McpToolCard
                v-for="toolId in selectedToolIds"
                :key="toolId"
                :serverId="selectedServerName"
                :toolId="toolId"
                :activeToolId="selectedToolId"
                @tool-selected="selectTool"
                @connect-oauth="openOAuthDialog"
              />
            </template>

            <div v-else class="text-body-2 text-medium-emphasis py-6">
              {{ toolPaneSubtitle }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <ExternalMcpServerCatalogDialog
      :show="showCatalogDialog"
      :existingServers="mcpServers"
      @close="showCatalogDialog = false"
      @install-existing="openInstallForExistingFromCatalog"
      @review="openInstallDialog"
    />
    <ExternalMcpServerInstallDialog
      :show="showInstallDialog"
      :draft="pendingInstallDraft"
      :existingServer="pendingExistingServer"
      :initialSecrets="pendingInstallInitialSecrets"
      @close="showInstallDialog = false"
      @installed="handleInstalled"
    />
    <ExternalMcpOAuthDialog
      :show="showOAuthDialog"
      :serverName="oauthServerName"
      @close="showOAuthDialog = false"
      @connected="handleOAuthConnected"
    />
    <ExternalMcpServerSecretsDialog
      :show="showSecretsDialog"
      :server="selectedSecretsServer"
      @close="showSecretsDialog = false"
      @saved="handleSecretsSaved"
    />
    <McpServerRequestOverlay
      :show="showRequestOverlay"
      :mode="requestOverlayMode"
      :initialServerName="requestOverlayServerName"
      @close="showRequestOverlay = false"
      @submitted="showRequestOverlay = false"
    />

    <v-dialog v-model="showDeleteSharedDialog" max-width="620" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>Delete Shared Server</span>
          <v-spacer />
          <v-btn icon variant="text" @click="closeDeleteSharedDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="deleteSharedError" type="error" density="compact" class="mb-3">
            {{ deleteSharedError }}
          </v-alert>

          <template v-if="deleteSharedImpact">
            <div class="mb-3">
              Deleting <strong>{{ deleteSharedImpact.serverName }}</strong> will remove the shared definition and all user-specific data for this server.
            </div>
            <div class="text-subtitle-2 mb-2">Impact</div>
            <div class="mb-1">Installed users: {{ deleteSharedImpact.installedUsers }}</div>
            <div class="mb-1">Connected OAuth users: {{ deleteSharedImpact.connectedAuthUsers }}</div>
            <div class="mb-1">Stored OAuth token users: {{ deleteSharedImpact.oauthTokenUsers }}</div>
            <div class="mb-1">Users with saved secrets: {{ deleteSharedImpact.usersWithSavedSecrets }}</div>
            <div class="mb-3">Active session users: {{ deleteSharedImpact.activeSessionUsers }}</div>
            <v-alert type="warning" density="compact" variant="tonal">
              This action is irreversible.
            </v-alert>
          </template>

          <div v-else-if="deleteSharedLoading" class="py-6 text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn variant="text" @click="closeDeleteSharedDialog">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleteSharedSubmitting"
            :disabled="!deleteSharedImpact"
            @click="confirmDeleteSharedServer"
          >
            Delete Shared Server
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import McpServerCard from '@/components/cards/McpServerCard.vue'
import UserMcpServerCard from '@/components/cards/UserMcpServerCard.vue'
import McpToolCard from '@/components/cards/McpToolCard.vue'
import McpServerCarousel from '@/components/mcp/McpServerCarousel.vue'
import ExternalMcpServerCatalogDialog from '@/components/dialogs/ExternalMcpServerCatalogDialog.vue'
import ExternalMcpServerInstallDialog from '@/components/dialogs/ExternalMcpServerInstallDialog.vue'
import ExternalMcpOAuthDialog from '@/components/dialogs/ExternalMcpOAuthDialog.vue'
import ExternalMcpServerSecretsDialog from '@/components/dialogs/ExternalMcpServerSecretsDialog.vue'
import McpServerRequestOverlay from '@/components/overlays/McpServerRequestOverlay.vue'
import { CreateExternalMcpServerRequest, UserVisibleMcpServer } from '@/types/mcp'
import type { AdminSharedServerDeleteImpact } from '@/types/api'
import { isApiError } from '@/types/api'
import { buildMcpServerCarouselItems, McpServerCarouselItem } from '@/utils/mcpCarousel'
import { compareUserVisibleMcpServers } from '@/utils/mcpServers'
import { Options, mixins } from 'vue-class-component'
import { mapGetters } from 'vuex'

@Options({
  components: {
    McpServerCard,
    UserMcpServerCard,
    McpToolCard,
    McpServerCarousel,
    ExternalMcpServerCatalogDialog,
    ExternalMcpServerInstallDialog,
    ExternalMcpOAuthDialog,
    ExternalMcpServerSecretsDialog,
    McpServerRequestOverlay
  },
  computed: {
    ...mapGetters(['mcpServers', 'mcpTools', 'mcpLoading', 'mcpError', 'isAdmin'])
  },
  watch: {
    isAdmin: {
      async handler(value: boolean) {
        if (value === true) {
          await (this as any).$store.dispatch('fetchAdminPlatformServers')
        }
      },
      immediate: true
    }
  }
})
export default class Mcp extends mixins(AnalyticsMixin) {
  mcpServers!: Record<string, UserVisibleMcpServer>
  mcpTools!: Record<string, Record<string, unknown>>
  mcpLoading!: boolean
  mcpError!: string | null
  isAdmin!: boolean

  selectedServerName: string | null = null
  selectedToolId: string | null = null
  showCatalogDialog = false
  showInstallDialog = false
  showOAuthDialog = false
  showSecretsDialog = false
  showRequestOverlay = false
  requestOverlayMode: 'platform_install' | 'external_removal' = 'platform_install'
  requestOverlayServerName = ''
  showDeleteSharedDialog = false
  oauthServerName = ''
  availableExternalExpanded = false
  pendingInstallDraft: CreateExternalMcpServerRequest | null = null
  pendingExistingServer: UserVisibleMcpServer | null = null
  pendingInstallInitialSecrets: Array<{ name: string; value: string }> = []
  selectedSecretsServer: UserVisibleMcpServer | null = null
  pendingDeleteSharedServerName = ''
  deleteSharedImpact: AdminSharedServerDeleteImpact | null = null
  deleteSharedLoading = false
  deleteSharedSubmitting = false
  deleteSharedError = ''

  async mounted() {
    await this.$store.dispatch('fetchUserMcpServers')
    if (this.isAdmin === true) {
      await this.$store.dispatch('fetchAdminPlatformServers')
    }

    this.trackComponentView('mcp_tab', {
      installed_external_server_count: this.installedExternalServers.length,
      available_external_server_count: this.availableExternalServers.length,
      platform_server_count: this.platformServers.length
    })
  }

  get alphabeticalServers(): UserVisibleMcpServer[] {
    return Object.values(this.mcpServers || {}).sort(compareUserVisibleMcpServers)
  }

  private moveSelectedExternalServerToTop(servers: UserVisibleMcpServer[]): UserVisibleMcpServer[] {
    if (!this.selectedServerName) {
      return servers
    }

    const selectedIndex = servers.findIndex((server) => server.name === this.selectedServerName)
    if (selectedIndex <= 0) {
      return servers
    }

    return [servers[selectedIndex], ...servers.slice(0, selectedIndex), ...servers.slice(selectedIndex + 1)]
  }

  get installedExternalServers(): UserVisibleMcpServer[] {
    return this.moveSelectedExternalServerToTop(
      this.alphabeticalServers.filter((server) => server.source === 'external' && server.installed)
    )
  }

  get availableExternalServers(): UserVisibleMcpServer[] {
    return this.moveSelectedExternalServerToTop(
      this.alphabeticalServers.filter((server) => server.source === 'external' && !server.installed)
    )
  }

  get platformServers(): UserVisibleMcpServer[] {
    return this.alphabeticalServers.filter((server) => server.source === 'platform')
  }

  get defaultCarouselItems(): McpServerCarouselItem[] {
    return buildMcpServerCarouselItems(this.alphabeticalServers)
  }

  get adminPlatformServerIds(): string[] {
    return this.platformServers.map((server) => server.name)
  }

  get selectedServer(): UserVisibleMcpServer | null {
    return this.selectedServerName ? this.mcpServers[this.selectedServerName] || null : null
  }

  get selectedToolIds(): string[] {
    return this.selectedServerName ? Object.keys(this.mcpTools?.[this.selectedServerName] || {}) : []
  }

  get toolPaneSubtitle(): string {
    if (!this.selectedServer) {
      return 'Select a server to view available tools'
    }
    if (this.selectedServer.source === 'external' && !this.selectedServer.installed) {
      return 'Install this server to add it to your account'
    }
    if (this.selectedServer.authMode === 'oauth2' && this.selectedServer.authState !== 'connected') {
      return 'Connect OAuth to load tools for this server'
    }
    if (this.selectedToolIds.length === 0) {
      return 'No tools available for this server'
    }
    return 'Select a server to view available tools'
  }

  async onServerSelected(serverName: string): Promise<void> {
    const selectedServer = this.mcpServers[serverName]
    this.trackButtonClick('select_mcp_server', 'click', {
      server_name: serverName,
      server_source: selectedServer?.source,
      auth_mode: selectedServer?.authMode,
      installed: selectedServer?.installed
    })

    this.selectedServerName = serverName
    this.selectedToolId = null

    await this.$store.dispatch('fetchUserServer', serverName)
    const server = this.mcpServers[serverName]
    if (!server) return

    if (server.source === 'external' && !server.installed) {
      this.availableExternalExpanded = true
    }

    if (server.source === 'external' && (!server.installed || (server.authMode === 'oauth2' && server.authState !== 'connected'))) {
      return
    }

    await this.$store.dispatch('fetchUserServerTools', serverName)
  }

  openAddExternalServerDialog(): void {
    this.trackButtonClick('open_add_external_mcp_server_catalog', 'click')
    this.showCatalogDialog = true
  }

  toggleAvailableExternalExpanded(): void {
    this.trackButtonClick('toggle_available_external_servers', this.availableExternalExpanded ? 'collapse' : 'expand', {
      available_external_server_count: this.availableExternalServers.length
    })
    this.availableExternalExpanded = !this.availableExternalExpanded
  }

  openPlatformRequestDialog(): void {
    this.trackButtonClick('request_platform_mcp_server_start', 'click')
    this.requestOverlayMode = 'platform_install'
    this.requestOverlayServerName = ''
    this.showRequestOverlay = true
  }

  openExternalRemovalRequestDialog(serverName: string): void {
    this.requestOverlayMode = 'external_removal'
    this.requestOverlayServerName = serverName
    this.showRequestOverlay = true
  }

  openInstallDialog(payload: {
    draft: CreateExternalMcpServerRequest
    existingServer: UserVisibleMcpServer | null
    initialSecrets?: Array<{ name: string; value: string }>
  }) {
    this.pendingInstallDraft = payload.draft
    this.pendingExistingServer = payload.existingServer
    this.pendingInstallInitialSecrets = payload.initialSecrets || []
    this.showCatalogDialog = false
    this.showInstallDialog = true
  }

  openInstallForExisting(serverName: string) {
    const server = this.mcpServers[serverName]
    if (!server) return

    this.trackButtonClick('install_mcp_server_start', 'click', {
      server_name: serverName,
      server_source: server.source,
      auth_mode: server.authMode
    })

    this.pendingExistingServer = server
    this.pendingInstallDraft = {
      name: server.name,
      displayName: server.displayName,
      description: server.description,
      url: server.url || '',
      authMode: server.authMode || 'none',
      oauthTemplate: server.oauthTemplate,
      secretFields: server.secretFields || [],
      homepageUrl: server.homepageUrl,
      repositoryUrl: server.repositoryUrl,
      licenseName: server.licenseName,
      oauthClientConfig: server.oauthClientConfig?.clientId
        ? {
            clientId: server.oauthClientConfig.clientId,
            scopes: server.oauthClientConfig.scopes
          }
        : undefined,
      enabled: true
    }
    this.pendingInstallInitialSecrets = []
    this.showInstallDialog = true
  }

  openInstallForExistingFromCatalog(serverName: string): void {
    this.showCatalogDialog = false
    this.openInstallForExisting(serverName)
  }

  async handleInstalled(payload: { server: UserVisibleMcpServer; needsOAuth: boolean }) {
    this.showInstallDialog = false
    this.pendingInstallInitialSecrets = []
    await this.$store.dispatch('fetchUserMcpServers')
    await this.onServerSelected(payload.server.name)
    if (payload.needsOAuth) {
      this.openOAuthDialog(payload.server.name)
    }
  }

  async uninstallServer(serverName: string) {
    await this.$store.dispatch('uninstallExternalServer', serverName)
    if (this.selectedServerName === serverName) {
      this.selectedServerName = null
    }
  }

  async deleteSharedExternalServer(serverName: string) {
    this.pendingDeleteSharedServerName = serverName
    this.deleteSharedImpact = null
    this.deleteSharedError = ''
    this.deleteSharedLoading = true
    this.showDeleteSharedDialog = true

    try {
      this.deleteSharedImpact = await this.$store.dispatch('fetchAdminSharedServerDeleteImpact', serverName)
    } catch (error) {
      this.deleteSharedError = isApiError(error) ? error.message : error instanceof Error ? error.message : 'Failed to load delete impact'
    } finally {
      this.deleteSharedLoading = false
    }
  }

  closeDeleteSharedDialog() {
    this.showDeleteSharedDialog = false
    this.pendingDeleteSharedServerName = ''
    this.deleteSharedImpact = null
    this.deleteSharedLoading = false
    this.deleteSharedSubmitting = false
    this.deleteSharedError = ''
  }

  async confirmDeleteSharedServer() {
    if (!this.pendingDeleteSharedServerName) {
      return
    }

    this.deleteSharedSubmitting = true
    this.deleteSharedError = ''

    try {
      const success = await this.$store.dispatch('deleteMcpServer', this.pendingDeleteSharedServerName)
      if (success !== true) {
        this.deleteSharedError = 'Failed to delete shared server'
        return
      }
      if (this.selectedServerName === this.pendingDeleteSharedServerName) {
        this.selectedServerName = null
        this.selectedToolId = null
      }
      this.closeDeleteSharedDialog()
    } catch (error) {
      this.deleteSharedError = isApiError(error) ? error.message : error instanceof Error ? error.message : 'Failed to delete shared server'
    } finally {
      this.deleteSharedSubmitting = false
    }
  }

  async enableServer(serverName: string) {
    await this.$store.dispatch('updateExternalServerInstall', { serverName, body: { enabled: true } })
    await this.$store.dispatch('fetchUserMcpServers')
  }

  async disableServer(serverName: string) {
    await this.$store.dispatch('updateExternalServerInstall', { serverName, body: { enabled: false } })
    await this.$store.dispatch('fetchUserMcpServers')
  }

  openOAuthDialog(serverName: string) {
    this.trackButtonClick('connect_mcp_oauth_start', 'click', {
      server_name: serverName
    })
    this.oauthServerName = serverName
    this.showOAuthDialog = true
  }

  async handleOAuthConnected(serverName: string) {
    this.showOAuthDialog = false
    await this.$store.dispatch('fetchUserMcpServers')
    await this.onServerSelected(serverName)
  }

  openSecretsDialog(serverName: string) {
    this.selectedSecretsServer = this.mcpServers[serverName] || null
    this.showSecretsDialog = true
  }

  async handleSecretsSaved(serverName: string) {
    this.showSecretsDialog = false
    await this.$store.dispatch('fetchUserServer', serverName)
  }

  selectTool(toolId: string) {
    this.trackButtonClick('select_mcp_tool', 'click', {
      tool_id: toolId,
      server_name: this.selectedServerName || undefined
    })
    this.selectedToolId = toolId
  }
}
</script>

<style scoped>
.mcp-page .section-title {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(var(--v-theme-on-surface), 0.7);
  margin: 12px 0;
  text-transform: uppercase;
}

.mcp-page .section-title-with-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mcp-page .section-title-toggle {
  cursor: pointer;
  user-select: none;
}

.mcp-page .section-title-toggle-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.mcp-page .section-title-count {
  font-size: 0.8rem;
  font-weight: 600;
}

.mcp-page .external-server-list {
  position: relative;
}

.mcp-page .external-server-list-move {
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
}
</style>
